import React from 'react';
import { useParams } from 'react-router-dom';

import * as GQL from '~/graphql/generated/client';

export type TemplateJobStatus =
  GQL.ReportJobStatusesQuery['reportJobStatuses'][0];

interface ReportJobsContextValue {
  // Templates data
  templates: GQL.ReportTemplatesQuery['reportTemplates'];
  templatesLoading: boolean;
  templatesError: any;
  getApplicableTemplates: (
    projectType: GQL.ProjectType
  ) => GQL.ReportTemplatesQuery['reportTemplates'];
  /** Re-read the template list — used when the server says one has gone. */
  refetchTemplates: () => void;
  // Job generation
  generate: (templateId: string) => Promise<void>;
}

const ReportJobsContext = React.createContext<
  ReportJobsContextValue | undefined
>(undefined);

interface ProviderProps {
  projectId: string;
  children: React.ReactNode;
}

export const ReportJobsProvider: React.FC<ProviderProps> = ({
  projectId,
  children,
}) => {
  const [generateReport] = GQL.useGenerateReportMutation();
  // Tracks in-flight generations so duplicate clicks (common on slow networks,
  // where the job status takes a moment to appear) don't enqueue extra jobs.
  const inFlightRef = React.useRef<Set<string>>(new Set());

  // Templates query
  const {
    data: templatesData,
    loading: templatesLoading,
    error: templatesError,
    refetch: refetchTemplates,
  } = GQL.useReportTemplatesQuery({
    /*
      network-only, not cache-and-network.
      Templates are created, replaced and deleted entirely outside this page —
      in the admin ingestion table, or straight in the database — so anything
      cached is a guess about someone else's data.
      cache-and-network was already an improvement on the cache-first default,
      but it paints the cached list FIRST and corrects a moment later. A card
      clicked inside that window sends a template id that may no longer exist,
      which is precisely the failure this page keeps producing. The list is a
      handful of rows; always asking is cheap, and it means the id under a card
      is never older than the page.
    */
    fetchPolicy: 'network-only',
  });

  const templates = React.useMemo(
    () => templatesData?.reportTemplates ?? [],
    [templatesData]
  );

  const getApplicableTemplates = React.useCallback(
    (projectType: GQL.ProjectType) => {
      return templates.filter((template) => {
        return template.applicableProjectTypes.includes(projectType);
      });
    },
    [templates]
  );

  const generate = React.useCallback(
    async (templateId: string) => {
      // Ignore duplicate generate requests for the same template while one is
      // already in flight.
      if (inFlightRef.current.has(templateId)) {
        return;
      }
      inFlightRef.current.add(templateId);
      try {
        await generateReport({ variables: { projectId, templateId } });
      } catch (e) {
        // eslint-disable-next-line no-console
        console.error('generate report failed', e);
        // Rethrow so callers can react. Swallowing it here left the card's
        // optimistic "Starting…" state waiting for a job that was never
        // queued: the button stayed disabled with nothing saying why.
        throw e;
      } finally {
        inFlightRef.current.delete(templateId);
      }
    },
    [generateReport, projectId]
  );

  const value: ReportJobsContextValue = React.useMemo(
    () => ({
      templates,
      templatesLoading,
      templatesError,
      getApplicableTemplates,
      generate,
      refetchTemplates,
    }),
    [
      templates,
      templatesLoading,
      templatesError,
      getApplicableTemplates,
      generate,
      refetchTemplates,
    ]
  );

  return (
    <ReportJobsContext.Provider value={value}>
      {children}
    </ReportJobsContext.Provider>
  );
};

export function useReportJobs() {
  const ctx = React.useContext(ReportJobsContext);
  if (!ctx) {
    throw new Error('useReportJobs must be used within ReportJobsProvider');
  }
  return ctx;
}

/**
 * Wrapper component that provides ReportJobsProvider with project-scoped data.
 * Extracts the effective project ID from route parameters and provides it to ReportJobsProvider.
 *
 * This follows the same pattern as DashboardDataProvider for determining the effective project ID.
 */
export const ReportJobsProviderWrapper: React.FC<{
  children: React.ReactNode;
}> = ({ children }) => {
  const { projectId, functionalModelId, candidateId, vendorModelId, agentId } =
    useParams();

  // Use the same logic as DashboardDataProvider to determine effective project ID
  const effectiveProjectId =
    candidateId ?? agentId ?? functionalModelId ?? vendorModelId ?? projectId;

  // Only provide ReportJobsProvider if we have a valid project ID
  if (!effectiveProjectId) {
    return <>{children}</>;
  }

  return (
    <ReportJobsProvider projectId={effectiveProjectId}>
      {children}
    </ReportJobsProvider>
  );
};
